import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";

interface SettingsData {
  environment: {
    siteUrl: string;
    pixExpirationSeconds: number;
    universalAuthUrl: string;
    universalApiUrl: string;
    accessTeamDomain: string;
  };
  integrations: {
    d1Database: { status: string; binding: string };
    r2Storage: { status: string; binding: string };
    kvSessions: { status: string; binding: string };
    kvRateLimit: { status: string; binding: string };
    resendEmail: { status: string; senderEmail?: string };
    turnstile: { status: string };
  };
  securityAndCompliance: {
    piiEncryption: string;
    sessionSigning: string;
    lgpdRetentionMonths: number;
    cfAccessMfa: string;
  };
}

export function AdminSettingsPage() {
  const [data, setData] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<SettingsData>("/admin/settings")
      .then((res) => setData(res))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar configurações."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div>
        <h2>Configurações do Sistema</h2>
        <p className="text-muted">Inspecionando serviços e integridade do ambiente...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <h2>Configurações do Sistema</h2>
        <p role="alert" className="notice-error">{error ?? "Não foi possível carregar as configurações."}</p>
      </div>
    );
  }

  const { environment, integrations, securityAndCompliance } = data;

  return (
    <div>
      <h2>Configurações e Diagnóstico do Sistema</h2>

      <section className="card" style={{ marginBottom: "var(--space-6)" }}>
        <h3>Status das Integrações Cloudflare e Infraestrutura</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Recurso</th>
              <th>Binding / Componente</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Banco Relacional Principal (D1)</td>
              <td><code>{integrations.d1Database.binding}</code></td>
              <td>
                <span className={`status-badge ${integrations.d1Database.status === "connected" ? "status-badge--paid" : "status-badge--negative"}`}>
                  {integrations.d1Database.status === "connected" ? "Conectado (Saudável)" : "Erro de Conexão"}
                </span>
              </td>
            </tr>
            <tr>
              <td>Armazenamento de Ebooks e Capas (R2)</td>
              <td><code>{integrations.r2Storage.binding}</code></td>
              <td>
                <span className={`status-badge ${integrations.r2Storage.status === "configured" ? "status-badge--paid" : "status-badge--pending"}`}>
                  {integrations.r2Storage.status === "configured" ? "Ativo (Privado)" : "Pendente"}
                </span>
              </td>
            </tr>
            <tr>
              <td>Armazenamento de Sessões Curtas (KV)</td>
              <td><code>{integrations.kvSessions.binding}</code></td>
              <td>
                <span className="status-badge status-badge--paid">Ativo</span>
              </td>
            </tr>
            <tr>
              <td>Controle de Taxa / Rate Limit (KV)</td>
              <td><code>{integrations.kvRateLimit.binding}</code></td>
              <td>
                <span className="status-badge status-badge--paid">Ativo</span>
              </td>
            </tr>
            <tr>
              <td>Disparo Transacional de E-mails (Resend)</td>
              <td><code>{integrations.resendEmail.senderEmail ?? "contato@datasci.com.br"}</code></td>
              <td>
                <span className="status-badge status-badge--paid">Configurado</span>
              </td>
            </tr>
            <tr>
              <td>Proteção Antibot (Cloudflare Turnstile)</td>
              <td><code>Turnstile Widget</code></td>
              <td>
                <span className="status-badge status-badge--paid">Configurado</span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="card" style={{ marginBottom: "var(--space-6)" }}>
        <h3>Parâmetros Operacionais e Pagamento Pix</h3>
        <dl style={{ display: "grid", gridTemplateColumns: "220px 1fr", rowGap: "var(--space-2)" }}>
          <dt><strong>URL Pública do Site:</strong></dt>
          <dd><code>{environment.siteUrl}</code></dd>

          <dt><strong>Expiração Padrão Pix:</strong></dt>
          <dd>{Math.round(environment.pixExpirationSeconds / 86400)} dias ({environment.pixExpirationSeconds} segundos)</dd>

          <dt><strong>Gateway Universal Pagamentos:</strong></dt>
          <dd><code>{environment.universalApiUrl}</code></dd>

          <dt><strong>Autenticação OAuth Gateway:</strong></dt>
          <dd><code>{environment.universalAuthUrl}</code></dd>

          <dt><strong>Domínio Cloudflare Access:</strong></dt>
          <dd><code>{environment.accessTeamDomain}</code></dd>
        </dl>
      </section>

      <section className="card">
        <h3>Segurança da Informação e Conformidade LGPD</h3>
        <dl style={{ display: "grid", gridTemplateColumns: "220px 1fr", rowGap: "var(--space-2)" }}>
          <dt><strong>Criptografia PII em Repouso:</strong></dt>
          <dd>{securityAndCompliance.piiEncryption}</dd>

          <dt><strong>Assinatura de Sessões:</strong></dt>
          <dd>{securityAndCompliance.sessionSigning}</dd>

          <dt><strong>Retenção Contratual B2B:</strong></dt>
          <dd>{securityAndCompliance.lgpdRetentionMonths} meses após encerramento do contrato</dd>

          <dt><strong>Políticas de Acesso Admin:</strong></dt>
          <dd>{securityAndCompliance.cfAccessMfa}</dd>
        </dl>
      </section>
    </div>
  );
}
